import type { FormType } from "@/components/auth/types"
import { createRouter, createWebHistory } from "vue-router"
import { RouteRole, UserRole } from "@/types/auth"
import { Layout } from "@/types/theme"
import { authCheck } from "@/utils/auth"
import AuthPage from "@/views/Auth.vue"
import Dashboard from "@/views/Dashboard.vue"

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: "/",
			redirect: "/dashboard"
		},
		{
			path: "/tools",
			redirect: "/tools/monitoring"
		},
		{
			path: "/dashboard",
			name: "Dashboard",
			component: Dashboard,
			meta: { title: "dashboard", auth: true, roles: RouteRole.ALL }
		},
		{
			path: "/patients",
			meta: {
				auth: true,
				roles: UserRole.DOCTOR
			},
			children: [
				{
					path: "",
					name: "PatientsList",
					component: () => import("@/views/Patients/ListWithToolbar.vue"),
					meta: { title: "patients" }
				},
				{
					path: "create",
					name: "PatientCreate",
					component: () => import("@/views/Patients/Create.vue"),
					meta: { title: "patient_create", skipPin: true }
				},
				{
					path: ":patientId",
					name: "PatientOverview",
					component: () => import("@/views/Patients/Overview.vue"),
					meta: { title: "patient_details", skipPin: true }
				},
				/* TODO-NEXT: MVP2 attivare routes per visite
				{
					path: ":patientId/visits",
					redirect: "create",
					children: [
						{
							path: "create",
							name: "VisitCreate",
							component: () => import("@/views/Visits/Create.vue"),
							meta: { title: "visit_create", skipPin: true }
						},
						{
							path: ":visitId",
							name: "VisitOverview",
							component: () => import("@/views/Visits/Details.vue"),
							meta: { title: "visit_details", skipPin: true }
						}
					]
				},
				{
					path: ":patientId/exams",
					redirect: "create",
					children: [
						{
							path: "create",
							name: "ExamCreate",
							component: () => import("@/views/Exams/Create.vue"),
							meta: { title: "exam_create", skipPin: true }
						},
						{
							path: ":examId",
							name: "ExamOverview",
							component: () => import("@/views/Exams/Details.vue"),
							meta: { title: "exam_details", skipPin: true }
						}
					]
				},
				*/
				{
					path: ":patientId/pregnancies",
					redirect: "create",
					children: [
						{
							path: "create",
							name: "PregnancyCreate",
							component: () => import("@/views/Pregnancies/Create.vue"),
							meta: { title: "pregnancy_create", skipPin: true }
						},
						{
							path: ":pregnancyId",
							name: "PregnancyOverview",
							component: () => import("@/views/Pregnancies/Details.vue"),
							meta: { title: "pregnancy_details", skipPin: true }
						}
					]
				}
				/* TODO-NEXT: MVP2 attivare routes per terapie
				{
					path: ":patientId/therapies",
					redirect: "create",
					children: [
						{
							path: "create",
							name: "TherapyCreate",
							component: () => import("@/views/Therapies/Create.vue"),
							meta: { title: "therapy_create", skipPin: true }
						},
						{
							path: ":therapyId",
							name: "TherapyOverview",
							component: () => import("@/views/Therapies/Details.vue"),
							meta: { title: "therapy_details", skipPin: true }
						}
					]
				}
				*/
			]
		},
		{
			path: "/calendar",
			name: "Calendar",
			component: () => import("@/views/Calendar.vue"),
			meta: { title: "calendar", auth: true, roles: UserRole.DOCTOR }
		},
		/* TODO-NEXT: MVP2 attivare routes per chat
		{
			path: "/chat",
			name: "Chat",
			component: () => import("@/views/Chat.vue"),
			meta: { title: "chat", auth: true, roles: UserRole.DOCTOR }
		},
		*/
		{
			path: "/tools",
			meta: {
				auth: true,
				roles: UserRole.DOCTOR
			},
			children: [
				{
					path: "monitoring",
					name: "Tools.Monitoring",
					component: () => import("@/views/Tools/Monitoring.vue"),
					meta: { title: "monitoring", auth: true, roles: UserRole.DOCTOR }
				},
				{
					path: "pregnancy-calculator",
					name: "Tools.PregnancyCalculator",
					component: () => import("@/views/Tools/PregnancyCalculator.vue"),
					meta: { title: "pregnancy_wheel", auth: true, roles: UserRole.DOCTOR }
				},
				{
					path: "biometry",
					name: "Tools.Biometry",
					component: () => import("@/views/Tools/Biometry.vue"),
					meta: { title: "fetal_biometry", auth: true, roles: UserRole.DOCTOR }
				}
				/* TODO-NEXT: MVP2 attivare routes per doppler
				{
					path: "doppler",
					name: "Tools.Doppler",
					component: () => import("@/views/Tools/Doppler.vue"),
					meta: { title: "doppler", auth: true, roles: UserRole.DOCTOR }
				}
				*/
			]
		},
		{
			path: "/profile",
			name: "Profile",
			component: () => import("@/views/Profile.vue"),
			meta: { title: "profile", auth: true, roles: UserRole.DOCTOR }
		},

		{
			path: "/login",
			name: "Login",
			component: AuthPage,
			props: { formType: "signin" as FormType },
			meta: {
				title: "login",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: true,
				skipPin: true
			}
		},
		{
			path: "/forgot-password",
			name: "ForgotPassword",
			component: AuthPage,
			props: { formType: "forgotpassword" as FormType },
			meta: {
				title: "forgot_password",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: true,
				skipPin: true
			}
		},
		{
			path: "/reset-password",
			name: "ResetPassword",
			component: AuthPage,
			props: { formType: "resetpassword" as FormType },
			meta: {
				title: "reset_password",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: true,
				skipPin: true
			}
		},
		{
			path: "/data-export",
			name: "DataExport",
			component: AuthPage,
			props: { formType: "dataexport" as FormType },
			meta: {
				title: "data_export",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: false,
				skipPin: true
			}
		},
		/* TODO-NEXT: MVP2 attivare routes per segretaria
		{
			path: "/secretary",
			name: "Secretary",
			component: AuthPage,
			props: { formType: "secretary" as FormType },
			meta: {
				title: "secretary_login",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: true,
				skipPin: true
			}
		},
		*/
		{
			path: "/register",
			name: "Register",
			component: () => import("@/views/Register.vue"),
			meta: {
				title: "register",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				checkAuth: true,
				skipPin: true
			}
		},
		{
			path: "/delete-account",
			name: "DeleteAccount",
			component: () => import("@/views/DeleteAccount.vue"),
			meta: { title: "delete_account", auth: true, roles: UserRole.DOCTOR }
		},
		{
			path: "/logout",
			name: "Logout",
			redirect: "/login"
		},
		{
			path: "/open/:pathMatch(.*)*",
			name: "OpenApp",
			component: () => import("@/views/OpenApp.vue"),
			meta: {
				title: "open_app",
				theme: { layout: Layout.Blank, boxed: { enabled: false }, padded: { enabled: false } },
				skipPin: true
			}
		},
		{
			path: "/:pathMatch(.*)*",
			name: "NotFound",
			component: () => import("@/views/NotFound.vue"),
			meta: {
				theme: {},
				skipPin: true
			}
		}
	]
})

router.beforeEach(route => {
	return authCheck(route)
})

export default router
